import { Component, OnInit, OnDestroy, TemplateRef } from '@angular/core'
import { collection, collectionData, orderBy, query, serverTimestamp, where, Query, DocumentData } from '@angular/fire/firestore'
import { BankDetails, Merchant, merchantsPath, Parking } from '@parkupp/core'
import { AdminService } from '../../../services/admin.service'
import * as firestore from 'firebase/firestore'
import { MerchantService } from '../../../services/merchant.service'
import { ParkingService } from '../../../services/parking.service'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import Swal from 'sweetalert2'
import { EnterpriseContextService } from '../../../services/enterprise-context.service'
import { Subscription } from 'rxjs'

@Component({
    selector: 'app-admin-list-merchants-page',
    templateUrl: './admin-list-merchants-page.component.html',
    styleUrls: ['./admin-list-merchants-page.component.scss'],
})
export class AdminListMerchantsPageComponent implements OnInit, OnDestroy {
    private enterpriseSubscription: Subscription
    merchants: Merchant[]
    allMerchants: Merchant[] = []
    isCreateMerchant = false
    isAdminCreateMerchant = false
    showDeletedItems = false
    contextMenuVisible = false
    contextMenuX = 0
    contextMenuY = 0
    selectedMerchant: Merchant | null = null
    currentSortColumn = ''
    currentSortDirection: 'asc' | 'desc' = 'asc'
    modalRef?: BsModalRef
    typeFilter: 'parkupp' | 'allocate' | 'all' = 'parkupp'

    columns = [
        { key: 'created', label: 'Created' },
        { key: 'type', label: 'Type' },
        { key: 'fee', label: 'Fee' },
        { key: 'name', label: 'Name' },
        { key: 'parkings', label: 'Parkings' },
        { key: 'banks', label: 'Bank Accounts' },
        { key: 'registration', label: 'Company Registration' },
        { key: 'vat', label: 'Vat Number' },
        { key: 'phone', label: 'Phone' },
        { key: 'responseTime', label: 'Response Time' },
    ]

    constructor(
        private adminService: AdminService, 
        private merchantService: MerchantService, 
        private parkingService: ParkingService, 
        private modalService: BsModalService,
        private enterpriseContext: EnterpriseContextService
    ) {}

    ngOnInit(): void {
        this.loadMerchants()
        
        // Subscribe to enterprise context changes
        this.enterpriseSubscription = this.enterpriseContext.currentEnterprise$.subscribe(() => {
            this.loadMerchants()
        })
    }
    
    ngOnDestroy(): void {
        if (this.enterpriseSubscription) {
            this.enterpriseSubscription.unsubscribe()
        }
    }
    
    loadMerchants(): void {
        const enterpriseKey = this.enterpriseContext.currentEnterpriseKey
        
        // Enterprise Managers MUST have an enterprise filter - skip if not set yet
        if (this.enterpriseContext.isEnterpriseManager && !enterpriseKey) {
            console.log('[Merchants] Skipping load - Enterprise Manager without enterprise key')
            return
        }
        
        let ref: Query<DocumentData> = query(collection(this.adminService.firestore, merchantsPath()), orderBy('createdAt', 'desc'))
        
        // Apply enterprise filter if set
        if (enterpriseKey) {
            ref = query(ref, where('denormEnterpriseKey', '==', enterpriseKey))
        }

        this.parkingService.list().subscribe((parkings: Parking[]) => {
            collectionData(ref, { idField: 'documentId' }).subscribe((documents: any[]) => {
                let merchants: Merchant[] = []
                documents.forEach((documentData) => {
                    let merchant = new Merchant(documentData)

                    merchant.$key = documentData.documentId

                    merchant.$parkings = parkings.filter((parking) => parking.merchantRef?.id === merchant.$key)

                    // Fetch the `bankDetails` subcollection
                    const bankDetailsRef = collection(this.adminService.firestore, `${merchantsPath()}/${merchant.$key}/bankDetails`)
                    collectionData(bankDetailsRef).subscribe((bankDetails) => {
                        if (bankDetails.length > 0) {
                            merchant.$bankDetails = bankDetails.map((bankDetail) => new BankDetails(bankDetail))
                        }
                    })

                    merchants.push(merchant)
                })
                this.allMerchants = merchants
                this.filterMerchants()
            })
        })
    }

    createMerchant(template: TemplateRef<any>) {
        this.modalRef = this.modalService.show(template)
    }

    onPortfolioCreated() {
        this.modalRef?.hide()
        // Refresh the data to show the new portfolio
        window.location.reload()
    }

    filterMerchants() {
        let filtered = this.allMerchants

        // Filter by deleted status
        if (this.showDeletedItems) {
            filtered = filtered.filter((merchant) => merchant.deletedAt)
        } else {
            filtered = filtered.filter((merchant) => !merchant.deletedAt)
        }

        // Filter by type (ParkUpp or Allocate)
        if (this.typeFilter === 'parkupp') {
            filtered = filtered.filter((merchant) => !merchant.bookuppEnabled)
        } else if (this.typeFilter === 'allocate') {
            filtered = filtered.filter((merchant) => merchant.bookuppEnabled)
        }

        this.merchants = filtered
    }

    toggleDeletedFilter() {
        this.showDeletedItems = !this.showDeletedItems
        this.filterMerchants()
    }

    setTypeFilter(type: 'parkupp' | 'allocate' | 'all') {
        this.typeFilter = type
        this.filterMerchants()
    }

    onRightClick(event: MouseEvent, merchant: Merchant) {
        event.preventDefault()
        this.contextMenuX = event.clientX
        this.contextMenuY = event.clientY
        this.selectedMerchant = merchant
        this.contextMenuVisible = true
    }

    hideContextMenu() {
        this.contextMenuVisible = false
        this.selectedMerchant = null
    }

    async deleteFromContextMenu() {
        if (this.selectedMerchant) {
            await this.deleteMerchant(this.selectedMerchant)
        }
        this.hideContextMenu()
    }

    async deleteMerchant(merchant: Merchant) {
        const result = await Swal.fire({
            title: 'Delete Portfolio?',
            text: `Are you sure you want to delete the portfolio "${merchant.companyName}"? This action will soft delete the portfolio.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        })

        if (result.isConfirmed) {
            try {
                await this.merchantService.softDeleteMerchant(merchant.$key!)

                // Update local data
                const merchantIndex = this.allMerchants.findIndex((m) => m.$key === merchant.$key)
                if (merchantIndex > -1) {
                    this.allMerchants[merchantIndex].deletedAt = serverTimestamp() as firestore.Timestamp
                }
                this.filterMerchants()

                await Swal.fire({
                    title: 'Deleted!',
                    text: 'The portfolio has been successfully deleted.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                })
            } catch (error) {
                console.error('Error deleting portfolio:', error)
                await Swal.fire({
                    title: 'Error!',
                    text: 'There was an error deleting the portfolio. Please try again.',
                    icon: 'error',
                })
            }
        }
    }

    sortByColumn(columnKey: string) {
        if (this.currentSortColumn === columnKey) {
            // Toggle direction if same column
            this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc'
        } else {
            // New column, default to ascending
            this.currentSortColumn = columnKey
            this.currentSortDirection = 'asc'
        }

        this.sortMerchants()
    }

    sortMerchants() {
        if (!this.currentSortColumn) return

        this.merchants.sort((a, b) => {
            let valueA: any
            let valueB: any

            switch (this.currentSortColumn) {
                case 'created':
                    valueA = a.createdAt?.seconds || 0
                    valueB = b.createdAt?.seconds || 0
                    break
                case 'type':
                    valueA = a.bookuppEnabled ? 'Allocate' : 'ParkUpp'
                    valueB = b.bookuppEnabled ? 'Allocate' : 'ParkUpp'
                    break
                case 'fee':
                    valueA = a.commissionPercentage || 15
                    valueB = b.commissionPercentage || 15
                    break
                case 'name':
                    valueA = (a.companyName || '').toLowerCase()
                    valueB = (b.companyName || '').toLowerCase()
                    break
                case 'parkings':
                    valueA = a.$parkings?.length || 0
                    valueB = b.$parkings?.length || 0
                    break
                case 'banks':
                    valueA = a.$bankDetails?.length || 0
                    valueB = b.$bankDetails?.length || 0
                    break
                case 'registration':
                    valueA = (a.companyRegistration || '').toLowerCase()
                    valueB = (b.companyRegistration || '').toLowerCase()
                    break
                case 'vat':
                    valueA = (a.vatNumber || '').toLowerCase()
                    valueB = (b.vatNumber || '').toLowerCase()
                    break
                case 'phone':
                    valueA = a.phone?.nationalNumber || ''
                    valueB = b.phone?.nationalNumber || ''
                    break
                case 'responseTime':
                    valueA = a.averageResponseTime || 0
                    valueB = b.averageResponseTime || 0
                    break
                default:
                    return 0
            }

            let comparison = 0
            if (valueA > valueB) {
                comparison = 1
            } else if (valueA < valueB) {
                comparison = -1
            }

            // Apply sort direction
            return this.currentSortDirection === 'asc' ? comparison : -comparison
        })
    }

    getSortIcon(columnKey: string): string {
        if (!columnKey || this.currentSortColumn !== columnKey) {
            return 'fa fa-sort'
        }
        return this.currentSortDirection === 'asc' ? 'fa fa-sort-up' : 'fa fa-sort-down'
    }

    // Stats card computed properties
    getTotalPortfolios(): number {
        return this.allMerchants.filter((m) => !m.deletedAt).length
    }

    getTotalParkings(): number {
        return this.allMerchants
            .filter((m) => !m.deletedAt)
            .reduce((sum, m) => sum + (m.$parkings?.length || 0), 0)
    }

    getWithBankDetails(): number {
        return this.allMerchants.filter((m) => !m.deletedAt && m.$bankDetails && m.$bankDetails.length > 0).length
    }

    getDeletedCount(): number {
        return this.allMerchants.filter((m) => m.deletedAt).length
    }
}
